import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useScroll,
  useSpring,
} from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, Phone } from "lucide-react";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { COMPANY_INFO, NAV_LINKS } from "../../utils/constants";
import { cn } from "../../utils/helpers";
import navLogo from "../../logos/nav-logo-lg-mode.png";

// Number shown in the navbar (desktop link and mobile "Call us").
const NAV_PHONE = COMPANY_INFO.phone2; // +919116885677
const NAV_PHONE_DISPLAY = NAV_PHONE.replace(/^\+91(\d{10})$/, "+91 $1");

// Two-line burger that morphs into an X.
function MenuIcon({ open }) {
  return (
    <span className="relative block w-5 h-3.5" aria-hidden="true">
      <motion.span
        animate={open ? { top: "50%", rotate: 45, y: "-50%" } : { top: 0, rotate: 0, y: 0 }}
        transition={{ duration: 0.25 }}
        className="absolute left-0 block h-0.5 w-full rounded-full bg-dark-900"
      />
      <motion.span
        animate={
          open
            ? { bottom: "50%", rotate: -45, y: "50%", width: "100%" }
            : { bottom: 0, rotate: 0, y: 0, width: "65%" }
        }
        transition={{ duration: 0.25 }}
        className="absolute right-0 block h-0.5 rounded-full bg-dark-900"
      />
    </span>
  );
}

export function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const { isScrolled } = useScrollPosition();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 30,
    restDelta: 0.001,
  });

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [isMobileMenuOpen]);

  const solid = isScrolled || isMobileMenuOpen;

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed inset-x-0 top-0 z-50 px-3 sm:px-4 transition-[padding] duration-300",
          isScrolled ? "pt-2" : "pt-3 md:pt-4",
        )}
      >
        <div
          className={cn(
            "relative mx-auto max-w-7xl overflow-hidden rounded-2xl transition-all duration-300",
            solid
              ? "bg-white/80 shadow-card ring-1 ring-dark-900/5 backdrop-blur-xl"
              : "bg-transparent",
          )}
        >
          <nav
            className={cn(
              "flex items-center justify-between gap-4 px-3 sm:px-4 lg:px-5 transition-[height] duration-300",
              isScrolled ? "h-14" : "h-16",
            )}
            aria-label="Main"
          >
            <Link to="/" className="flex shrink-0 items-center" aria-label="Techoside home">
              <img src={navLogo} alt="Techoside" className="h-8 sm:h-9 w-auto" />
            </Link>

            {/* Desktop links */}
            <ul
              className="hidden lg:flex items-center gap-1 rounded-full bg-dark-900/[0.03] p-1 ring-1 ring-dark-900/5"
              onMouseLeave={() => setHovered(null)}
            >
              {NAV_LINKS.map((link) => (
                <li key={link.path} onMouseEnter={() => setHovered(link.path)}>
                  <NavLink
                    to={link.path}
                    end={link.path === "/"}
                    className={({ isActive }) =>
                      cn(
                        "relative block rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                        isActive ? "text-white" : "text-dark-600 hover:text-dark-900",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.span
                            layoutId="nav-active"
                            className="absolute inset-0 rounded-full bg-dark-900 shadow-md"
                            transition={{ type: "spring", stiffness: 400, damping: 34 }}
                          />
                        )}
                        {!isActive && hovered === link.path && (
                          <motion.span
                            layoutId="nav-hover"
                            className="absolute inset-0 rounded-full bg-white shadow-soft"
                            transition={{ type: "spring", stiffness: 400, damping: 34 }}
                          />
                        )}
                        <span className="relative">{link.name}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2">
              <a
                href={`tel:${NAV_PHONE}`}
                className="hidden xl:inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-dark-600 hover:text-primary-600 transition-colors"
              >
                <Phone className="w-4 h-4" />
                {NAV_PHONE_DISPLAY}
              </a>
              <Link
                to="/contact"
                className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 py-2 pl-4 pr-2 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all hover:shadow-xl hover:shadow-primary-500/30 hover:-translate-y-0.5"
              >
                Get a Quote
                <span className="grid place-items-center w-7 h-7 rounded-full bg-white/20 transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </Link>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                className="lg:hidden grid place-items-center w-11 h-11 rounded-xl bg-white/70 ring-1 ring-dark-900/5 hover:bg-white transition-colors"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <MenuIcon open={isMobileMenuOpen} />
              </button>
            </div>
          </nav>

          {/* Scroll progress */}
          <motion.div
            aria-hidden="true"
            style={{ scaleX: progress }}
            className={cn(
              "absolute inset-x-0 bottom-0 h-0.5 origin-left bg-gradient-to-r from-primary-500 via-accent-blue to-primary-500 transition-opacity duration-300",
              isScrolled && !isMobileMenuOpen ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 lg:hidden overflow-y-auto bg-white"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary-300/30 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-accent-blue/10 blur-3xl"
            />
            <div className="relative flex min-h-full flex-col px-6 pt-28 pb-8">
              <nav aria-label="Mobile" className="flex-1">
                <ul className="space-y-1">
                  {NAV_LINKS.map((link, index) => (
                    <motion.li
                      key={link.path}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ delay: 0.05 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <NavLink
                        to={link.path}
                        end={link.path === "/"}
                        className={({ isActive }) =>
                          cn(
                            "group flex items-center justify-between border-b border-dark-100 py-4 font-display text-3xl font-semibold tracking-tight transition-colors",
                            isActive ? "text-primary-600" : "text-dark-900 hover:text-primary-600",
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span className="flex items-baseline gap-3">
                              <span className="font-sans text-xs font-semibold text-dark-300">
                                {String(index + 1).padStart(2, "0")}
                              </span>
                              {link.name}
                            </span>
                            <ArrowRight
                              className={cn(
                                "w-6 h-6 transition-all",
                                isActive
                                  ? "opacity-100"
                                  : "opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0",
                              )}
                            />
                          </>
                        )}
                      </NavLink>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-10 space-y-4"
              >
                <Link
                  to="/contact"
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 font-semibold text-white shadow-lg shadow-primary-500/25"
                >
                  Get a Quote
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center gap-2 rounded-2xl bg-dark-50 p-3 font-medium text-dark-700 ring-1 ring-dark-100"
                  >
                    <Mail className="w-4 h-4 shrink-0 text-primary-500" />
                    <span className="truncate">Email us</span>
                  </a>
                  <a
                    href={`tel:${NAV_PHONE}`}
                    className="flex items-center gap-2 rounded-2xl bg-dark-50 p-3 font-medium text-dark-700 ring-1 ring-dark-100"
                  >
                    <Phone className="w-4 h-4 shrink-0 text-primary-500" />
                    <span className="truncate">Call us</span>
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
